import { SearchIcon } from '@chakra-ui/icons';
import {
  Center,
  Flex,
  Wrap
} from "@chakra-ui/layout";
import {
  Box, Button, ButtonGroup, Icon, IconButton, Input, InputGroup,
  InputLeftElement,
  InputRightElement,
  Portal,
  Stack,
  Text,
  useColorModeValue,
  useMediaQuery
} from "@chakra-ui/react";
import { useWindowWidth } from '@react-hook/window-size';
import { AiButton } from 'components/common/AiButton/AiButton';
import { Suggestions } from "components/suggestions";
import { useSuggestions } from "components/suggestions/useSuggestions";
import { useSession } from "next-auth/react";
import React, { useEffect, useRef, useState } from "react";
import { BsArrowRightShort } from "react-icons/bs";
import PuffLoader from "react-spinners/PuffLoader";
import { textStyle, variant } from "theme/names";
import { useDebouncedCallback } from "use-debounce";
import { useOnClickOutside } from "../../utils/helpers";
import { Filter } from "./filter";

const SearchBar = ({
  sendRequest,
  placeholder,
  aiButton = false,
  isLoading,
  isSending,
  setIsSending,
  setData = null as any,
  nbMinRt = null as any,
  setNbMinRt = null as any,
  nbMinLikes = null as any,
  setNbMinLikes = null as any,
  nbMaxDaysOld = null as any,
  setNbMaxDaysOld = null as any,
  nbMinDaysOld = null as any,
  setNbMinDaysOld = null as any,
  nbMinFollowers = null as any,
  setNbMinFollowers = null as any,
  nbMaxFollowers = null as any,
  setNbMaxFollowers = null as any,
  noHashtag = null as any,
  setNoHashtag = null as any,
  noDollar = null as any,
  setNoDollar = null as any,
  noCrypto = null as any,
  setNoCrypto = null as any,
  noThread = null as any,
  setNoThread = null as any,
  onlyThread = null as any,
  setOnlyThread = null as any,
  includeTwitter = null as any,
  setIncludeTwitter = null as any,
  includeLinkedin = null as any,
  setIncludeLinkedin = null as any,
  includeCarrousel = null as any,
  setIncludeCarrousel = null as any,
  disableFilters = false,
  hasCustomFilters = false,
  searchMode = "search",
  suggestions = null as any,
  mt = 10,
  size = "lg",
  showButtonIcon = false,
  disableShadow = true,
  defaultValue = "",
  disableLocalSave = false,
  showButton = false,
  showLoader = false,
  align = "left",
  withHandleSuggestions = false,
  suggestionPrefix = "@",
  buttonText = "",
  showSearchButtonRightIcon = true,
  searchButtonLeftIcon = undefined as any,
  showSearchIconInInput = true,
  disableButtonOnEmptyInput = false,
  inputWidth = "100%"
}) => {
  const { data: session, status } = useSession() ?? {}
  const loading = status === "loading"
  const screenWidth = useWindowWidth();

  const [topic, setTopic] = React.useState<string>("");
  const [hasInit, setHasInit] = React.useState<boolean>(false);
  const [isSmallerThan600px] = useMediaQuery("(max-width: 600px)");
  // const { onOpen, onClose, isOpen } = useDisclosure();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const refFilters = React.useRef<any>();
  const debounced = useDebouncedCallback(checkSuggestions, 400);
  const inputRef = useRef<HTMLInputElement>(null)
  const backgroundColor = useColorModeValue("white", "gray.800");
  const {
    isSuggestionsOpen,
    setIsSuggestionOpen,
    checkForSuggestions,
    suggestions: suggestionsList,
    disableArrowKeys,
    updateText,
    setSuggestions
  } = useSuggestions(inputRef);

  const suggestionPopupRef = useRef(null)
  useOnClickOutside(suggestionPopupRef, async () => {
    await new Promise(resolve => setTimeout(resolve, 200));
    setIsSuggestionOpen(false);
    if (inputRef?.current?.focus) {
      inputRef.current.focus();
    }
  });
  useOnClickOutside(refFilters, async () => { await new Promise(resolve => setTimeout(resolve, 200)); setIsOpen(false); });

  useEffect(() => {

    let savedSearch = localStorage.getItem(`searchTopic_${searchMode}`);

    if (savedSearch && savedSearch != "undefined")
      setTopic(savedSearch);

  }, []);

  useEffect(() => {

    if (session?.user?.uid && !hasInit) {
      setHasInit(true);

      if ((["engage", "ideas"].includes(searchMode))
        && session?.user?.data?.keywords?.join
      ) {
        setTopic(session.user.data.keywords.join(","));
      }
    }

  }, [session]);

  function checkSuggestions(event) {
    if (!withHandleSuggestions) {
      return
    }

    if (!isSearching) {
      checkForSuggestions(event, suggestionPrefix)
    }
  }

  function handleSelect(handle: string) {
    updateText({ currentText: topic, handle }, (updatedText) => {
      setTopic(updatedText)
    })
  }

  const carrouselOptions = [
    { value: 1, label: 'Carousels Only' },
    { value: 0, label: 'All Types' },
    { value: -1, label: 'Exclude Carousels' }
  ]

  return (
    <Flex
      flexDirection="column"
      alignItems={
        align === "left"
          ? "flex-start"
          : "center"
      }
      w="100%"
    >
      <Flex flexDirection="column" alignItems="center"
        width={["100%", "500px"]}
      >
        <Stack
          width="100%"
          as="form"
          onSubmit={(e) => {
            e.preventDefault();
            setIsSearching(true);
            sendRequest(session, topic);
          }}
          isInline
          mb={size == "lg" ? 2 : 0}
          mt={mt}
        >
          <InputGroup
            height="50px"
            size={size}
          >
            {showSearchIconInInput && (
              <InputLeftElement
                pointerEvents="none"
                children={<SearchIcon color="gray.300" />}
              />
            )}
            <Input
              width={inputWidth}
              fontSize={"16px"}
              ref={inputRef}
              disabled={isLoading}
              placeholder={placeholder}
              value={topic}
              boxShadow={disableShadow ? "none" : "dark-lg"}
              onChange={(e) => {
                debounced(e)
                setIsSearching(false);
                setTopic(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.keyCode === 27) {
                  setIsSuggestionOpen(false);
                  setSuggestions([]);
                }
              }}
              onKeyDown={disableArrowKeys}
            />
            {
              withHandleSuggestions && isSuggestionsOpen && suggestionsList.length > 0 && (
                <Box
                  position="absolute"
                  zIndex="9"
                  maxH="44"
                  overflow="auto"
                  background={backgroundColor}
                  borderRadius="md"
                  border="1px"
                  boxShadow="md"
                  width="100%"
                  borderColor="inherit"
                  padding="1"
                  top="105%"
                  tabIndex={1}
                  ref={suggestionPopupRef}
                >
                  <Suggestions
                    suggestions={suggestionsList}
                    onSelect={handleSelect}
                  />
                </Box>
              )
            }
            {
              !disableFilters && (
                <InputRightElement width="4.5rem">

                  <Box>
                    <IconButton
                      aria-label="Search database"
                      size="lg"
                      variant="ghost"
                      borderRadius="18px"
                      icon={<Icon viewBox="0 0 16 15">
                        <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M0 12.5C0 12.0938 0.3125 11.75 0.75 11.75H2.59375C2.90625 10.75 3.875 10 5 10C6.09375 10 7.0625 10.75 7.375 11.75H15.25C15.6562 11.75 16 12.0938 16 12.5C16 12.9375 15.6562 13.25 15.25 13.25H7.375C7.0625 14.2812 6.09375 15 5 15C3.875 15 2.90625 14.2812 2.59375 13.25H0.75C0.3125 13.25 0 12.9375 0 12.5ZM6 12.5C6 11.9688 5.53125 11.5 5 11.5C4.4375 11.5 4 11.9688 4 12.5C4 13.0625 4.4375 13.5 5 13.5C5.53125 13.5 6 13.0625 6 12.5ZM11 5C12.0938 5 13.0625 5.75 13.375 6.75H15.25C15.6562 6.75 16 7.09375 16 7.5C16 7.9375 15.6562 8.25 15.25 8.25H13.375C13.0625 9.28125 12.0938 10 11 10C9.875 10 8.90625 9.28125 8.59375 8.25H0.75C0.3125 8.25 0 7.9375 0 7.5C0 7.09375 0.3125 6.75 0.75 6.75H8.59375C8.90625 5.75 9.875 5 11 5ZM12 7.5C12 6.96875 11.5312 6.5 11 6.5C10.4375 6.5 10 6.96875 10 7.5C10 8.0625 10.4375 8.5 11 8.5C11.5312 8.5 12 8.0625 12 7.5ZM15.25 1.75C15.6562 1.75 16 2.09375 16 2.5C16 2.9375 15.6562 3.25 15.25 3.25H8.375C8.0625 4.28125 7.09375 5 6 5C4.875 5 3.90625 4.28125 3.59375 3.25H0.75C0.3125 3.25 0 2.9375 0 2.5C0 2.09375 0.3125 1.75 0.75 1.75H3.59375C3.90625 0.75 4.875 0 6 0C7.09375 0 8.0625 0.75 8.375 1.75H15.25ZM5 2.5C5 3.0625 5.4375 3.5 6 3.5C6.53125 3.5 7 3.0625 7 2.5C7 1.96875 6.53125 1.5 6 1.5C5.4375 1.5 5 1.96875 5 2.5Z" fill="currentColor" />
                        </svg>
                      </Icon>}
                      onClick={() => { setIsOpen(!isOpen); }}
                    >
                    </IconButton>
                    {
                      hasCustomFilters && (
                        <Icon w={2.5} h={2.5} position="absolute" right="24px" top="12px" viewBox="0 0 200 200" color="#0A66C2">
                          <path
                            fill="currentColor"
                            d="M 100, 100 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0"
                          />
                        </Icon>
                      )
                    }
                  </Box>
                  {
                    isOpen && (
                      <Box>
                        <Portal  >
                          <Box
                            position="absolute"
                            top={screenWidth < 800 ? "290px" : "230px"}
                            right={`${Math.max((screenWidth / 2 - 260), 0)}px`}
                            bg={backgroundColor}
                            boxShadow="rgba(100, 100, 111, 0.15) 0px 2px 19px 0px;"
                            rounded="md"
                            zIndex="1"
                            p={8}
                            ref={refFilters}
                          >
                            <Stack maxWidth="300px" minWidth="300px" alignItems="left">
                              <Flex alignItems="center">
                                <Icon viewBox="0 0 16 15" mr="2">
                                  <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 12.5C0 12.0938 0.3125 11.75 0.75 11.75H2.59375C2.90625 10.75 3.875 10 5 10C6.09375 10 7.0625 10.75 7.375 11.75H15.25C15.6562 11.75 16 12.0938 16 12.5C16 12.9375 15.6562 13.25 15.25 13.25H7.375C7.0625 14.2812 6.09375 15 5 15C3.875 15 2.90625 14.2812 2.59375 13.25H0.75C0.3125 13.25 0 12.9375 0 12.5ZM6 12.5C6 11.9688 5.53125 11.5 5 11.5C4.4375 11.5 4 11.9688 4 12.5C4 13.0625 4.4375 13.5 5 13.5C5.53125 13.5 6 13.0625 6 12.5ZM11 5C12.0938 5 13.0625 5.75 13.375 6.75H15.25C15.6562 6.75 16 7.09375 16 7.5C16 7.9375 15.6562 8.25 15.25 8.25H13.375C13.0625 9.28125 12.0938 10 11 10C9.875 10 8.90625 9.28125 8.59375 8.25H0.75C0.3125 8.25 0 7.9375 0 7.5C0 7.09375 0.3125 6.75 0.75 6.75H8.59375C8.90625 5.75 9.875 5 11 5ZM12 7.5C12 6.96875 11.5312 6.5 11 6.5C10.4375 6.5 10 6.96875 10 7.5C10 8.0625 10.4375 8.5 11 8.5C11.5312 8.5 12 8.0625 12 7.5ZM15.25 1.75C15.6562 1.75 16 2.09375 16 2.5C16 2.9375 15.6562 3.25 15.25 3.25H8.375C8.0625 4.28125 7.09375 5 6 5C4.875 5 3.90625 4.28125 3.59375 3.25H0.75C0.3125 3.25 0 2.9375 0 2.5C0 2.09375 0.3125 1.75 0.75 1.75H3.59375C3.90625 0.75 4.875 0 6 0C7.09375 0 8.0625 0.75 8.375 1.75H15.25ZM5 2.5C5 3.0625 5.4375 3.5 6 3.5C6.53125 3.5 7 3.0625 7 2.5C7 1.96875 6.53125 1.5 6 1.5C5.4375 1.5 5 1.96875 5 2.5Z" fill="currentColor" />
                                  </svg>
                                </Icon>
                                <Text textStyle={textStyle['h2.bold.standard']} mr={5}>Advanced Filters</Text>
                              </Flex>
                              <Filter title="Minimum likes" prefix="at least" max={1000} step={10} value={nbMinLikes} setValue={setNbMinLikes} />
                              <Filter
                                title="Published"
                                prefix="at least"
                                sufix="days ago"
                                max={1000}
                                min={30}
                                step={1}
                                value={nbMinDaysOld || 30}
                                setValue={(val) => {
                                  setNbMinDaysOld(val);
                                  if (nbMaxDaysOld !== null && nbMaxDaysOld < val) {
                                    setNbMaxDaysOld(val);
                                  }
                                }}
                              />
                              <Filter
                                title="Published"
                                prefix="at most"
                                sufix="days ago"
                                max={2000}
                                min={nbMinDaysOld || 30}
                                step={1}
                                value={nbMaxDaysOld !== null ? Math.max(nbMaxDaysOld, nbMinDaysOld || 30) : nbMaxDaysOld}
                                setValue={setNbMaxDaysOld}
                              />
                              <Filter title="Min. followers" prefix="at least" max={10000} step={100} value={nbMinFollowers} setValue={setNbMinFollowers} />
                              <Filter title="Max. followers" prefix="at most" max={1000000} step={100} value={nbMaxFollowers} setValue={setNbMaxFollowers} />

                              <ButtonGroup
                                variant="light"
                                isAttached
                                py={5}
                                borderLeft="1"
                                borderRight="1"
                              >
                                {carrouselOptions.map(({ value, label }) => {
                                  return (
                                    <Filter
                                      key={value}
                                      type="button"
                                      title={label}
                                      value={value}
                                      isActive={value === includeCarrousel}
                                      setValue={() => setIncludeCarrousel(value)}
                                    />
                                  )
                                })}
                              </ButtonGroup>

                              <Filter type="checkbox" title="No Hashtag" value={noHashtag} setValue={setNoHashtag} />
                              <Filter type="checkbox" title="No Ticker" value={noDollar} setValue={setNoDollar} />
                              <Filter type="checkbox" title="No Crypto" value={noCrypto} setValue={setNoCrypto} />

                              <Filter type="checkbox" title="Include Tweets" value={includeTwitter} setValue={setIncludeTwitter} />
                              <Filter type="checkbox" title="Include Linkedin posts" value={includeLinkedin} setValue={setIncludeLinkedin} />

                              <Center >
                                <Button
                                  mt={5}
                                  mr={3}
                                  variant="secondary"
                                  onClick={() => {
                                    setNbMinLikes?.(40);
                                    setNbMinRt?.(0);
                                    setNbMinDaysOld?.(30);
                                    setNbMaxDaysOld?.(1000);
                                    setNbMinFollowers?.(2000);
                                    setNbMaxFollowers?.(100000);
                                    setNoHashtag?.(false);
                                    setNoDollar?.(false);
                                    setNoCrypto?.(true);
                                    setIncludeTwitter?.(true);
                                    setIncludeLinkedin?.(true);
                                    setIncludeCarrousel?.(0);
                                  }}
                                >
                                  Reset
                                </Button>
                                <Button
                                  mt={5}
                                  mr={3}
                                  variant="primary"
                                  onClick={() => {
                                    setIsOpen(false);
                                    sendRequest(session, topic);
                                  }}
                                >Apply</Button>
                              </Center>
                            </Stack>
                          </Box>
                        </Portal>
                      </Box>
                    )
                  }
                </InputRightElement>
              )
            }
          </InputGroup>
          {
            showButton && !aiButton && (
              <Button
                variant="primary"
                isDisabled={disableButtonOnEmptyInput ? !topic : false}
                isLoading={isLoading}
                minW="auto"
                p={5}
                rightIcon={showSearchButtonRightIcon ? <BsArrowRightShort fontSize="20px" /> : undefined}
                leftIcon={searchButtonLeftIcon ? <Icon viewBox="0 0 16 16" h="4" w="4">{searchButtonLeftIcon}</Icon> : undefined}
                onClick={() => {
                  sendRequest(session, topic);
                }}
              >
                {buttonText || "Search"}
              </Button>
            )
          }
          {
            showButton && aiButton && (
              <AiButton
                isDisabled={disableButtonOnEmptyInput ? !topic : false}
                isLoading={isLoading}
                minW="auto"
                containerProps={{
                  minW: "180px"
                }}
                onClick={() => {
                  sendRequest(session, topic);
                }}
              >
                {buttonText || "Search"}
              </AiButton>
            )
          }
        </Stack>
        {
          suggestions?.length > 0 ? (isSmallerThan600px ? (
            <Stack
              width="100%"
              mb={4}
              mt={2}
            >
              <Wrap mb={0} mt={0} width="100%" justify="center">
                {suggestions?.map((trendingTopic: string) => (
                  <Button
                    variant={variant.Button.suggestion}
                    key={trendingTopic}
                    onClick={() => {
                      console.log(`trendingTopic: ${trendingTopic}`);
                      setTopic(trendingTopic);
                      sendRequest(session, trendingTopic);
                    }}
                  >
                    {trendingTopic}
                  </Button>
                ))}
              </Wrap>
            </Stack>
          ) : (
            <Stack
              width="100%"
              justifyContent="center"
              mb={4}
              mt={2}
            >
              <Wrap mb={0} mt={0} width="100%" justify="center" spacing="8px">
                {suggestions?.map((trendingTopic: string) => (
                  <Button
                    variant={variant.Button.rounded}
                    key={trendingTopic}
                    onClick={() => {
                      setTopic(trendingTopic);
                      sendRequest(session, trendingTopic);
                    }}
                  >
                    {trendingTopic}
                  </Button>
                ))}
              </Wrap>
            </Stack>
          )) : null
        }
      </Flex>
      {
        showLoader && isLoading && (
          <Center mt={10} pt={10} w="100%">
            <PuffLoader color={"#0A66C2"} loading={isSending} cssOverride={{ display: "block", margin: "0 auto", borderColor: "red" }} size={50} />
          </Center>
        )
      }
    </Flex>
  );
};

export default SearchBar;
